import {
  CameraListRequest,
  FrameStreamRequest,
  CameraByPrinterIdRequest
} from './grpc_protocol_pb';
import {CameraViewServiceClient} from './grpc_protocol_grpc_web_pb';
import {runAsync} from './common.js';

//IE support
import textDecoder from 'text-encoding';
if (!window['TextDecoder']) {
  window['TextDecoder'] = textDecoder;
}

class CameraStreamService {
  #service;
  #listStreamListeners;
  #frameStreamListeners;

  constructor(hostname) {
    this.#service = new CameraViewServiceClient(hostname);
    this.#listStreamListeners = [];
    this.#frameStreamListeners = [];
  }

  /**
   * Start camera list streaming
   *
   * @param {string} camera_list_token
   * @param {function} callback
   * @param {function} error_callback
   * @returns {object} !grpc.web.ClientReadableStream<!proto.CameraStatus>
   */
  streamCameraList(camera_list_token, callback, error_callback) {
    let request = new CameraListRequest();
    request.setCameraListToken(camera_list_token);
    let stream = this.#service.cameraList(request/*, createRequestMetadata()*/);
    const listeners = this.#listStreamListeners;
    stream.on('data', function(response) {
      CameraStreamService._handleStreamEvent('data', response, callback, listeners);
    });
    stream.on('error', function(error) {
      CameraStreamService._handleStreamEvent('error', error, error_callback, listeners);
    });
    stream.on('end', function(end) {
      CameraStreamService._handleStreamEvent('end', end, error_callback, listeners);
    });

    return stream;
  }

  /**
   * Start camera frame streaming
   *
   * @param {string} camera_token
   * @param {function} callback
   * @param {function} error_callback
   * @param {mixin} additionalCallbackData
   * @returns {object} !grpc.web.ClientReadableStream<!proto.Frame>
   */
  streamCameraFrame(camera_token, callback, error_callback, additionalCallbackData) {
    let request = new FrameStreamRequest();
    request.setFrameStreamToken(camera_token);
    let streams = this.#service.frameStream(request/*, createRequestMetadata()*/);
    const listeners = this.#frameStreamListeners;
    streams.on('data', function(response) {
      if (additionalCallbackData) {
        response.additionalCallbackData = additionalCallbackData;
      }
      CameraStreamService._handleStreamEvent('data', response, callback, listeners);
    });
    streams.on('error', function(error) {
      CameraStreamService._handleStreamEvent('error', error, error_callback, listeners);
    });
    streams.on('end', function(end) {
      CameraStreamService._handleStreamEvent('end', end, error_callback, listeners);
    });

    return streams;
  }

  /**
   * Register camera list changes listener.
   * Listen all stream events (data, error, end). Event type will be in second argument.
   *
   * @param {function} listener
   */
  registerCameraListListener(listener) {
    this.#listStreamListeners.push(listener);
  }

  /**
   * Register changing camera frame listener.
   * Listen all stream events (data, error, end). Event type will be in second argument.
   *
   * @param {function} listener
   */
  registerCameraFrameListener(listener) {
    this.#frameStreamListeners.push(listener);
  }

  /**
   * Clear list listeners
   */
  clearCameraListListeners() {
    this.#frameStreamListeners = [];
  }

  /**
   * Clear frame listeners
   */
  clearCameraFrameListeners() {
    this.#frameStreamListeners = [];
  }

  /**
   * Get printer's current camera data
   * @param {string} printerId
   * @param {function} callback
   * @param {function} errorCallback
   */
  getCameraByPrinterId(printerId, callback, errorCallback) {
    let request = new CameraByPrinterIdRequest();
    request.setPrinterId(printerId);
    this.#service.cameraByPrinterId(request, {}, function(err, response) {
      if (err) {
        errorCallback(err);
      } else {
        callback(response);
      }
    });
  }

  /**
   * Broadcast stream event to callback and listeners
   * @param {string} type
   * @param {object} data
   * @param {function} callback
   * @param {Array} listeners
   */
  static _handleStreamEvent(type, data, callback, listeners) {
    if (callback) {
      runAsync(callback, data, type);
    }
    CameraStreamService._updateListeners(listeners, data, type);
  }

  /**
   * Send event to all registered listeners
   * @param {Array} listeners
   * @param {object} data
   * @param {string} type
   */
  static _updateListeners(listeners, data, type) {
    if (listeners.length) {
      for (let i in listeners) {
        runAsync(listeners[i], data, type);
      }
    }
  }
}

class GrpcServicePool {
  #serverPool;

  constructor(grpcServer, length) {
    this.#serverPool = [];
    this.#serverPool.push(grpcServer);
    const dot1 = grpcServer.indexOf('.');
    if (grpcServer.indexOf('.', dot1+1) >= 0) {
      const subdomain = grpcServer.substr(0, dot1);
      const domain = grpcServer.substr(dot1);
      for (let i = 1; i < length; i++) {
        this.#serverPool.push(subdomain + i + domain);
      }
    }
  }

  allocateGrpcService() {
    const MAX_CONNECTIONS = 6;
    const KEY_PREFIX = 'grpcConn';
    //Find available connection number
    let idx = 0;
    const infinity = true;
    while (infinity) {
      const connLastActivity = localStorage.getItem(KEY_PREFIX + idx);
      if (connLastActivity === null || Date.now() - connLastActivity > 11000) {
        break;
      }
      idx++;
    }
    if (idx >= this.#serverPool.length * 6) {
      console.log("Browser connections limit exceeded. Please, close your other 3DprinterOS tabs with camera view.");
    }
    //Allocate connection number
    window.localStorage.setItem(KEY_PREFIX + idx, window.Date.now());

    //Get hostname by connection number
    const hostname = this.#serverPool[Math.floor(idx / MAX_CONNECTIONS) % this.#serverPool.length]; //Maximum 6 connections for one host

    //Create gRPC service
    const cameraStreamService = new CameraStreamService(hostname);
    function updateTTL(data, type) {
      if (type === 'data') {
        window.localStorage.setItem(KEY_PREFIX + idx, Date.now());
      } else {
        window.localStorage.removeItem(KEY_PREFIX + idx);
      }
    }
    cameraStreamService.registerCameraListListener(updateTTL);
    cameraStreamService.registerCameraFrameListener(updateTTL);

    return cameraStreamService;
  }
}

export {CameraStreamService, GrpcServicePool};